// PartnerBillSummaryComponent.js
import React, { useState, useEffect } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import LoadingSpinner from "../../../Loading/component";
import styles from "./PartnerBillSummary.module.css";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import billsummery from "../../../../assests/billsummery.svg";
import dayWiseData from "../../../../assests/daysWiseData.svg";
import chargeicon from "../../../../assests/chargeicon.svg";
import cancel from "../../../../assests/cancel.svg";
import leftArrow from "../../../../assests/leftArrow.svg";


const PartnerBillSummaryComponent = ({
  loading,
  billData,
  onGoBack,
  payoutBillData,
  handleMiscCharge,
  fetchChargeBillSummery,
  chargeSummery,
  handlePostBill,
  partnerId,
  allCharges,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [showDeductionDetails, setShowDeductionDetails] = useState(false);
  const [calculatedEndDate, setCalculatedEndDate] = useState("");
  const [calculatedStartDate, setCalculatedStartDate] = useState("");
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [selectedChargeId, setSelectedChargeId] = useState(null);
  const [activeChargeTab, setActiveChargeTab] = useState("bill");

  useEffect(() => {
    if (billData || payoutBillData) {
      const startDate = calculateStartDate();
      const endDate = calculateEndDate(startDate);
      setCalculatedStartDate(startDate);
      setCalculatedEndDate(endDate);
    }
  }, [billData, payoutBillData]);

  const getChargeDetailsById = (chargeId) => {
    if (!allCharges || !Array.isArray(allCharges)) return null;
    return allCharges.find((charge) => charge.id === chargeId);
  };

  const selectedChargeDetails = getChargeDetailsById(selectedChargeId);

  const getLastDateOfMonth = (dateString) => {
    try {
      if (
        !dateString ||
        dateString === "N/A" ||
        dateString === "null" ||
        dateString === "undefined"
      ) {
        return "N/A";
      }

      let date;
      if (dateString.includes("/")) {
        const [day, month, year] = dateString.split("/").map(Number);
        date = new Date(year, month - 1, day);
      } else if (dateString.includes("-")) {
        date = new Date(dateString);
      } else {
        return "N/A";
      }

      if (isNaN(date.getTime())) return "N/A";

      const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const formattedDay = String(lastDate.getDate()).padStart(2, "0");
      const formattedMonth = String(lastDate.getMonth() + 1).padStart(2, "0");
      const formattedYear = lastDate.getFullYear();

      return `${formattedDay}/${formattedMonth}/${formattedYear}`;
    } catch (error) {
      console.error("Error calculating last date of month:", error);
      return "N/A";
    }
  };

  const formatDateToDDMMYYYY = (dateString) => {
    if (
      !dateString ||
      dateString === "null" ||
      dateString === "undefined" ||
      dateString === "N/A"
    ) {
      return "N/A";
    }

    try {
      let date;
      if (dateString.includes("-")) {
        date = new Date(dateString);
      } else if (dateString.includes("/")) {
        return dateString;
      } else {
        return dateString;
      }

      if (isNaN(date.getTime())) return "N/A";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const calculateStartDate = () => {
    if (
      billData?.startDate &&
      billData.startDate !== "null" &&
      billData.startDate !== "undefined" &&
      billData.startDate !== "N/A"
    ) {
      return formatDateToDDMMYYYY(billData.startDate);
    }
    return "N/A";
  };

  const calculateEndDate = (startDate) => {
    if (
      billData?.endDate &&
      billData.endDate !== "null" &&
      billData.endDate !== "undefined" &&
      billData.endDate !== "N/A"
    ) {
      return formatDateToDDMMYYYY(billData.endDate);
    }
    if (startDate && startDate !== "N/A") {
      const monthEndDate = getLastDateOfMonth(startDate);
      if (monthEndDate !== "N/A") return monthEndDate;
    }
    return "N/A";
  };

  const getDisplayStartDate = () => {
    if (billData?.startDate) return formatDateToDDMMYYYY(billData.startDate);
    return "N/A";
  };

  const getDisplayEndDate = () => {
    if (
      billData?.endDate &&
      billData.endDate !== "null" &&
      billData.endDate !== "undefined"
    ) {
      return formatDateToDDMMYYYY(billData.endDate);
    }
    const startDate = getDisplayStartDate();
    if (startDate && startDate !== "N/A") {
      const monthEndDate = getLastDateOfMonth(startDate);
      if (monthEndDate !== "N/A") return monthEndDate;
    }
    return "N/A";
  };

  if (loading) return <LoadingSpinner />;
  if (!billData) return <div>No bill data available.</div>;

  const handleViewBillChargeClick = (charge) => {
    setSelectedCharge(charge);
    setSelectedChargeId(charge.id);
    setActiveChargeTab("bill");
    setOpenDialog(true);
  };

  const handleViewPartnerChargeClick = (charge) => {
    setSelectedCharge(charge);
    setSelectedChargeId(charge.id);
    setActiveChargeTab("partner");
    setOpenDialog(true);
  };

  const totalTransactionalAmount = chargeSummery?.transactionalCharges?.reduce(
    (acc, charge) => acc + charge.amount,
    0,
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    })
      .format(amount)
      .replace("₹", "₹ ");
  };

  const formatChargeTypeLabel = (chargeType) => {
    if (!chargeType) return "N/A";
    if (chargeType === "raas_fee") return "RAAS Fee (%)";
    if (chargeType === "ps_fee") return "PS Fee (%)";
    if (chargeType === "saas_ik") return "SAAS IK";
    if (chargeType === "rental_ik") return "Rental IK";
    if (chargeType === "misc_charge") return "Miscellaneous";
    if (chargeType === "debit") return "Debit";
    return chargeType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatValueType = (valueType) => {
    if (!valueType) return "N/A";
    if (valueType === "amount_percentage") return "Percentage (%)";
    if (valueType === "amount_flat") return "Flat Amount";
    if (valueType === "fixed") return "Fixed";
    if (valueType === "percentage") return "Percentage (%)";
    return valueType.replace(/_/g, " ");
  };

  const totalFixedAmount = chargeSummery?.fixedCharges?.reduce(
    (acc, charge) => acc + charge.amount,
    0,
  );

  const totalDeduction =
    (totalTransactionalAmount || 0) + (totalFixedAmount || 0);

  const selectedDayNetPayable =
    selectedDayIndex !== null
      ? payoutBillData?.payouts?.[selectedDayIndex]?.netPayable || 0
      : 0;

  const netDeduction = totalDeduction + selectedDayNetPayable;
  const isNetPayableNegative = selectedDayNetPayable < 0;

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDeductionClick = (payoutId, totalDeductions, dayIndex) => {
    setSelectedDayIndex(dayIndex);
    if (totalDeductions >= 0) {
      fetchChargeBillSummery(payoutId);
      setShowDeductionDetails(true);
    }
  };

  const formatChargeSummaryDate = () => {
    if (chargeSummery?.date) {
      const date = new Date(chargeSummery.date);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      }
    }
    return "N/A";
  };

  const hasBillId =
    billData?.billId &&
    billData.billId !== "null" &&
    billData.billId !== "undefined" &&
    billData.billId !== "N/A";

  const billCharges = billData?.charges || [];
  const partnerCharges = Array.isArray(allCharges) ? allCharges : [];

  return (
    <div className={styles.partnerBillLayout}>
      <Sidebar />
      <div className={styles.partnerBillSummary}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className={styles.backButtonpayoutDetails}
            onClick={onGoBack}
          >
            <img src={leftArrow} alt="" />
          </button>
          <h2 className={styles.partnerTitle}>
            Partner- {billData.name} (Owner ID:{billData.franchiseeId})
          </h2>
        </div>

        <div className={styles.billIdBanner}>
          <div className={styles.billIdLeft}>
            Bill ID - <span> {billData.billId}</span>
          </div>
          <div className={styles.billIdLeft}>
            Total VM: <span>{payoutBillData?.vmCount || 0}</span>
          </div>
          <div className={styles.billIdRight}>
            <img src={billsummery} alt="" /> Start Date:
            <span>{getDisplayStartDate()}</span>
            &nbsp; End Date:
            <span>{getDisplayEndDate()}</span>
          </div>
        </div>

        {/* ============================================================
            COMBINED CHARGES SECTION
            ============================================================ */}
        <div className={styles.billChargesSection}>
          <div className={styles.chargesHeader}>
            <h4>
              <img src={chargeicon} alt="" />
              Charges
            </h4>
            {billData.status !== "CLOSED" && (
              <Button
                variant="outlined"
                className={styles.addChargeBtn}
                onClick={handleMiscCharge}
              >
                Add Misc. Charge
              </Button>
            )}
          </div>

          {/* ---------- SUB SECTION: BILL CHARGES ---------- */}
          <div className={styles.chargesSubsection}>
            <div className={styles.chargesSubheader}>
              <span className={styles.chargesSubheaderIcon}>📄</span>
              <span className={styles.chargesSubheaderText}>
                Charges Applied on this Bill
              </span>
              <span className={styles.chargesSubheaderCount}>
                {billCharges.length}
              </span>
            </div>

            <div className={styles.chargesContainer}>
              {billCharges.length > 0 ? (
                billCharges.map((charge, i) => (
                  <div
                    className={styles.chargeBanner}
                    key={`bill-${charge.id}-${i}`}
                  >
                    {charge.chargeType === "misc_charge" && (
                      <div className={styles.miscBadge}>M</div>
                    )}

                    <div className={styles.chargeDetails}>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>ID:</span>
                        <span className={styles.chargeValue}>
                          {charge.id}
                        </span>
                      </div>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>Name:</span>
                        <span
                          className={styles.chargeValue}
                          title={charge.name || charge.chargeType}
                        >
                          {charge.name ||
                            formatChargeTypeLabel(charge.chargeType)}
                        </span>
                      </div>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>Type:</span>
                        <span
                          className={`${styles.chargeValue} ${styles.chargeValueBadge}`}
                        >
                          {formatChargeTypeLabel(charge.chargeType)}
                        </span>
                      </div>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>Amount:</span>
                        <span
                          className={`${styles.chargeValue} ${styles.chargeValueAmount}`}
                        >
                          {charge.valueType === "amount_percentage" ||
                          charge.valueType === "percentage"
                            ? `${charge.amount}%`
                            : formatCurrency(charge.amount || 0)}
                        </span>
                      </div>
                    </div>

                    <IconButton
                      className={styles.chargeviewIcon}
                      onClick={() => handleViewBillChargeClick(charge)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))
              ) : (
                <div className={styles.chargesEmptyText}>
                  No charges applied on this bill
                </div>
              )}
            </div>
          </div>

          <div className={styles.chargesDivider} />

          {/* ---------- SUB SECTION: PARTNER CHARGES ---------- */}
          <div className={styles.chargesSubsection}>
            <div className={styles.chargesSubheader}>
              <span className={styles.chargesSubheaderIcon}>⚙️</span>
              <span className={styles.chargesSubheaderText}>
                Partner Charges Configuration
              </span>
              <span className={styles.chargesSubheaderCount}>
                {partnerCharges.length}
              </span>
            </div>

            <div className={styles.chargesContainer}>
              {partnerCharges.length > 0 ? (
                partnerCharges.map((charge, i) => (
                  <div
                    className={`${styles.chargeBanner} ${styles.partnerChargeBanner}`}
                    key={`partner-${charge.id}-${i}`}
                  >
                    <div className={styles.chargeDetails}>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>ID:</span>
                        <span className={styles.chargeValue}>
                          {charge.id}
                        </span>
                      </div>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>Name:</span>
                        <span
                          className={styles.chargeValue}
                          title={charge.name || "N/A"}
                        >
                          {charge.name || "N/A"}
                        </span>
                      </div>
                      <div className={styles.chargeRow}>
                        <span className={styles.chargeLabel}>Value:</span>
                        <span
                          className={`${styles.chargeValue} ${styles.chargeValueAmount}`}
                        >
                          {charge.valueType === "amount_percentage"
                            ? `${charge.value}%`
                            : formatCurrency(Number(charge.value) || 0)}
                        </span>
                      </div>
                    </div>

                    <IconButton
                      className={styles.chargeviewIcon}
                      onClick={() => handleViewPartnerChargeClick(charge)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))
              ) : (
                <div className={styles.chargesEmptyText}>
                  No partner charges configured
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============================================================
            CHARGE DETAILS DIALOG
            ============================================================ */}
        <Dialog
          open={openDialog}
          className={styles.chargeDialog}
          PaperProps={{
            style: {
              borderRadius: "12px",
              padding: "20px",
              minWidth: "450px",
              maxWidth: "600px",
            },
          }}
        >
          <DialogTitle
            className={styles.dialogTitle}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "12px",
              padding: "16px 24px",
            }}
          >
            <img src={chargeicon} alt="Charge Icon" />
            <div>
              <Typography
                variant="h5"
                style={{ fontWeight: 600, fontSize: "20px" }}
              >
                {activeChargeTab === "bill"
                  ? selectedCharge?.name ||
                    formatChargeTypeLabel(selectedCharge?.chargeType) ||
                    "Charge Details"
                  : selectedChargeDetails?.name ||
                    selectedCharge?.name ||
                    "Charge Details"}
              </Typography>
              <Typography
                variant="subtitle2"
                style={{ color: "#666", marginTop: "4px" }}
              >
                {activeChargeTab === "bill"
                  ? "Bill Charge"
                  : "Partner Configuration Charge"}{" "}
                · Charge ID:{" "}
                {selectedChargeDetails?.id || selectedCharge?.id}
              </Typography>
            </div>
          </DialogTitle>

          <DialogContent
            className={styles.dialogContent}
            sx={{ padding: "16px 24px" }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {activeChargeTab === "bill" && selectedCharge ? (
                <>
                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge ID:</span>
                    <span className={styles.detailValue}>
                      {selectedCharge.id}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge Name:</span>
                    <span className={styles.detailValue}>
                      {selectedCharge.name || "N/A"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge Type:</span>
                    <span className={styles.detailValue}>
                      {formatChargeTypeLabel(selectedCharge.chargeType)}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Description:</span>
                    <span className={styles.detailValue}>
                      {selectedCharge.description ||
                        "No description available"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Value Type:</span>
                    <span className={styles.detailValue}>
                      {formatValueType(selectedCharge.valueType)}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Amount:</span>
                    <span
                      className={styles.detailValue}
                      style={{ fontWeight: 600 }}
                    >
                      {selectedCharge.valueType === "amount_percentage" ||
                      selectedCharge.valueType === "percentage"
                        ? `${selectedCharge.amount}%`
                        : formatCurrency(selectedCharge.amount || 0)}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Minimum Value:</span>
                    <span className={styles.detailValue}>
                      {selectedCharge.valueMin !== null &&
                      selectedCharge.valueMin !== undefined
                        ? selectedCharge.valueType === "amount_percentage"
                          ? `${selectedCharge.valueMin}%`
                          : formatCurrency(selectedCharge.valueMin)
                        : "Not Set"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Maximum Value:</span>
                    <span className={styles.detailValue}>
                      {selectedCharge.valueMax !== null &&
                      selectedCharge.valueMax !== undefined
                        ? selectedCharge.valueType === "amount_percentage"
                          ? `${selectedCharge.valueMax}%`
                          : formatCurrency(selectedCharge.valueMax)
                        : "Not Set"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge ID:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.id || selectedCharge?.id}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge Name:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.name ||
                        selectedCharge?.name ||
                        "N/A"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Charge Type:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.chargeType ||
                        selectedCharge?.chargeType ||
                        "N/A"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>
                      Deduction Type ID:
                    </span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.deductionTypeId || "N/A"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Description:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.description ||
                        "No description available"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Value Type:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.valueType || "N/A"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Value Amount:</span>
                    <span
                      className={styles.detailValue}
                      style={{ fontWeight: 600 }}
                    >
                      {selectedChargeDetails?.value
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.value}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.value) || 0,
                            )
                        : formatCurrency(selectedCharge?.amount || 0)}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Minimum Value:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.valueMin
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.valueMin}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.valueMin) || 0,
                            )
                        : "Not Set"}
                    </span>
                  </div>

                  <div className={styles.chargeDetailItem}>
                    <span className={styles.detailLabel}>Maximum Value:</span>
                    <span className={styles.detailValue}>
                      {selectedChargeDetails?.valueMax
                        ? selectedChargeDetails.valueType?.includes(
                            "percentage",
                          )
                          ? `${selectedChargeDetails.valueMax}%`
                          : formatCurrency(
                              Number(selectedChargeDetails.valueMax) || 0,
                            )
                        : "Not Set"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </DialogContent>

          <DialogActions
            style={{ justifyContent: "center", padding: "20px 24px" }}
          >
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              className={styles.chargeDialogCloseBtn}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============================================================
            DEDUCTION DETAILS DIALOG
            ============================================================ */}
        <Dialog
          open={showDeductionDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: { maxHeight: "90vh", overflow: "hidden" },
          }}
        >
          <div className={styles.deductionDialogHeader}>
            <DialogTitle style={{ padding: 0 }}>
              {formatChargeSummaryDate()}
              {chargeSummery?.payoutId
                ? ` (Payout ID ${chargeSummery.payoutId})`
                : ""}
            </DialogTitle>
            <IconButton
              onClick={() => setShowDeductionDetails(false)}
              style={{ padding: 8 }}
            >
              <img src={cancel} alt="Close" style={{ width: 24, height: 24 }} />
            </IconButton>
          </div>

          <DialogContent className={styles.deductionDialogContent}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                <div className={styles.deductionDetailBox}>
                  <div className={styles.deductionPartnerInfo}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <strong>Partner Name</strong> –{" "}
                        {chargeSummery?.name || "N/A"} (
                        {chargeSummery?.type || "N/A"})
                        <br />
                        <strong>Cohort ID</strong> –{" "}
                        {chargeSummery?.cohortId || "N/A"}
                      </div>
                      <div>
                        <strong>Partner Id</strong> –{" "}
                        {chargeSummery?.franchiseeId || "N/A"}
                        <br />
                        <strong>Total VM</strong> –{" "}
                        {chargeSummery?.vmCount || "N/A"}
                      </div>
                    </div>
                  </div>

                  <h4 className={styles.deductionTableTitle}>
                    Charge Summary (Transactional)
                  </h4>
                  <TableContainer
                    component={Paper}
                    className={styles.deductionTableContainer}
                  >
                    <Table size="small" style={{ border: "none" }}>
                      <TableHead>
                        <TableRow className={styles.deductionTableHeadRow}>
                          <TableCell className={styles.deductionTableHeadCell}>
                            Deduction ID
                          </TableCell>
                          <TableCell className={styles.deductionTableHeadCell}>
                            Description
                          </TableCell>
                          <TableCell
                            align="right"
                            className={styles.deductionTableHeadCell}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chargeSummery?.transactionalCharges?.map((charge) => (
                          <TableRow
                            key={`transactional-${charge.id}`}
                            style={{ border: "none" }}
                          >
                            <TableCell
                              className={styles.deductionTableCell}
                            >
                              {charge.id}
                            </TableCell>
                            <TableCell
                              className={styles.deductionTableCell}
                            >
                              {charge.chargeType === "raas_fee"
                                ? "RAAS Fee(%)"
                                : charge.chargeType === "ps_fee"
                                  ? "PS Fee(%)"
                                  : charge.chargeType}
                            </TableCell>
                            <TableCell
                              align="right"
                              className={styles.deductionTableCell}
                            >
                              {formatCurrency(charge.amount)}
                            </TableCell>
                          </TableRow>
                        ))}

                        <TableRow className={styles.deductionTableTotalRow}>
                          <TableCell
                            colSpan={2}
                            className={styles.deductionTableCell}
                          >
                            % Charge Total
                          </TableCell>
                          <TableCell
                            align="right"
                            className={styles.deductionTableCell}
                          >
                            {formatCurrency(totalTransactionalAmount || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <h4 className={styles.deductionTableTitle}>
                    Charge Summary (Fixed)
                  </h4>
                  <TableContainer
                    component={Paper}
                    className={styles.deductionTableContainer}
                  >
                    <Table size="small" style={{ border: "none" }}>
                      <TableHead>
                        <TableRow className={styles.deductionTableHeadRow}>
                          <TableCell className={styles.deductionTableHeadCell}>
                            Deduction ID
                          </TableCell>
                          <TableCell className={styles.deductionTableHeadCell}>
                            Description
                          </TableCell>
                          <TableCell
                            align="right"
                            className={styles.deductionTableHeadCell}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chargeSummery?.fixedCharges?.map((charge) => (
                          <TableRow
                            key={`fixed-${charge.id}`}
                            style={{ border: "none" }}
                          >
                            <TableCell
                              className={styles.deductionTableCell}
                            >
                              {charge.id}
                            </TableCell>
                            <TableCell
                              className={styles.deductionTableCell}
                            >
                              {charge.chargeType === "saas_ik"
                                ? "SAAS IK"
                                : charge.chargeType === "rental_ik"
                                  ? "Rental IK"
                                  : charge.chargeType === "misc_charge"
                                    ? "Miscellaneous"
                                    : charge.chargeType}
                            </TableCell>
                            <TableCell
                              align="right"
                              className={styles.deductionTableCell}
                            >
                              {formatCurrency(charge.amount)}
                            </TableCell>
                          </TableRow>
                        ))}

                        <TableRow className={styles.deductionTableTotalRow}>
                          <TableCell
                            colSpan={2}
                            className={styles.deductionTableCell}
                          >
                            Fix Charge Total
                          </TableCell>
                          <TableCell
                            align="right"
                            className={styles.deductionTableCell}
                          >
                            {formatCurrency(totalFixedAmount || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <div className={styles.totalDeductionBox}>
                    Total Deduction (Transactional + Fixed) ={" "}
                    {formatCurrency(
                      (chargeSummery?.totalTransactionalDeductions || 0) +
                        (chargeSummery?.totalFixedDeductions || 0),
                    )}
                  </div>

                  {isNetPayableNegative && (
                    <div className={styles.totalDeductionBox}>
                      Net Deduction (Total Deduction + Net Payout) ={" "}
                      {formatCurrency(netDeduction)}
                      <span className={styles.netDeductionNote}>
                        (Net Payout: {formatCurrency(selectedDayNetPayable)})
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ============================================================
            SUMMARY SECTION
            ============================================================ */}
        <div className={styles.summarySection}>
          <div className={styles.summaryHeader}>
            <h4>
              <img src={billsummery} alt="" /> Bill Summary -{" "}
              <span>{getDisplayStartDate()}</span>{" "}
              <span>to</span>{" "}
              <span>{getDisplayEndDate()}</span>
              <span
                className={`${styles.statusBadgepayout} ${
                  styles[billData.status?.toLowerCase()] || ""
                }`}
              >
                {" "}
                {billData.status}{" "}
              </span>
            </h4>
            {hasBillId && (
              <a className={styles.pastBillsLink} onClick={handlePostBill}>
                View Past Bills
              </a>
            )}
          </div>
          <div className={styles.summaryCards}>
            <div className={styles.summaryCard}>
              <div>Total Gross Sale</div>
              <strong>
                {formatCurrency(payoutBillData?.totalGrossSale || 0)}
              </strong>
            </div>
            <div className={styles.summaryCard}>
              <div>Total Fix Deduction</div>
              <strong>
                {formatCurrency(payoutBillData?.totalFixedDeductions || 0)}
              </strong>
            </div>
            <div className={styles.summaryCard}>
              <div>Total Transactional Deduction</div>
              <strong>
                {formatCurrency(
                  payoutBillData?.totalTransactionalDeductions || 0,
                )}
              </strong>
            </div>
            <div className={styles.summaryCard}>
              <div>Net Payout</div>
              <strong
                style={{
                  color:
                    (payoutBillData?.totalNetPayable || 0) < 0
                      ? "#d32f2f"
                      : "#2e7d32",
                }}
              >
                {formatCurrency(payoutBillData?.totalNetPayable || 0)}
              </strong>
            </div>
          </div>
        </div>

        {/* ============================================================
            DAY-WISE TABLE
            ============================================================ */}
        <div className={styles.tableSection}>
          <h4>
            <img src={dayWiseData} alt="" /> Day Wise Payout Details
          </h4>
          <div className={styles.tableWrapperSummery}>
            <table className={styles.payoutTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Settlement ID</th>
                  <th>Total Gross Sale</th>
                  <th>Net Payout</th>
                  <th>Total Deduction</th>
                </tr>
              </thead>
              <tbody>
                {payoutBillData &&
                  payoutBillData?.payouts?.map((day, i) => (
                    <tr key={i}>
                      <td>{day.date}</td>
                      <td>{day.payoutId}</td>
                      <td>{formatCurrency(Number(day.revenueToday) || 0)}</td>
                      <td
                        style={{
                          color:
                            (day.netPayable || 0) < 0 ? "#d32f2f" : "#2e7d32",
                        }}
                      >
                        {formatCurrency(day.netPayable || 0)}
                      </td>
                      <td>
                        <span
                          className={styles.deductionCell}
                          onClick={() => {
                            handleDeductionClick(
                              day.payoutId,
                              day.netPayable < 0
                                ? day.revenueToday
                                : day.totalDeductions,
                              i,
                            );
                          }}
                        >
                          {formatCurrency(
                            day.netPayable < 0
                              ? day.revenueToday
                              : day.totalDeductions,
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerBillSummaryComponent;